import React, { useRef, useState } from "react";
import Box from "@mui/material/Box";
import styled from "@emotion/styled";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { theme, linkColor } from "@/theme";
import { ArrowButton } from "./CarouselButtons";

import {
  getArrowShape,
  getArrowColor,
  getArrowPadding,
  getDotsPadding,
  getDotsPlacement,
  getSlidePositioning,
} from "./carouselUtils";

import { CarouselProps, Arrows, Dots } from "./Carousel.types";

const CarouselDotsWrapper = styled.div<{ paddingOnWrapper: boolean }>`
  display: flex;
  left: 0;
  right: 0;
  position: absolute;
  z-index: 10;
  ${({ paddingOnWrapper }) =>
    paddingOnWrapper && `padding: ${theme.spacing(3)} ${theme.spacing(1)};`}
`;

const CarouselDot = styled.button`
  height: 12px;
  width: 12px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid ${linkColor};
  margin: 0 4px;
  transition: opacity ease-in-out 250ms;
  background: transparent;

  &:hover {
    opacity: 0.5;
    cursor: pointer;
  }

  &:focus-visible {
    box-shadow: 0px 2px 8px 2px hsl(var(--color-gray-900) / 0.4);
  }

  &.active {
    background: ${theme.palette.primary.main};
  }
`;

export const Carousel = ({
  items = [],
  arrows = { gutter: "sm", variant: "blue-icon" },
  dots = { gutter: "lg", placement: "bottom", paddingOnWrapper: true },
  slideGap = true,
  slidePosition = "center",
  startAtIndex = 0,
  onSlideChange,
  carouselItemClass,
  loop = true,
  breakpoints,
  speed,
  effect,
}: CarouselProps) => {
  const prevRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(startAtIndex);
  const [swiperInstance, setSwiperInstance] = useState<any>(null);

  const spaceBetween = slideGap ? 16 : 0;
  const showArrows = arrows !== "none";
  const showDots = dots !== "none";

  const arrowShape = showArrows
    ? getArrowShape((arrows as Arrows).variant)
    : "none";
  const arrowColor = showArrows
    ? getArrowColor((arrows as Arrows).variant)
    : "blue";
  const arrowPadding = showArrows
    ? getArrowPadding((arrows as Arrows).gutter)
    : theme.spacing(0);

  const dotsPadding = showDots
    ? getDotsPadding((dots as Dots).gutter)
    : theme.spacing(0);
  const dotsStyle = showDots ? getDotsPlacement((dots as Dots).placement) : {};
  const slideStyle = getSlidePositioning(slidePosition);

  const gutterPadding = `${theme.spacing(0)} ${arrowPadding}`;
  const slidePadding = slideGap
    ? `${theme.spacing(0)} ${theme.spacing(1)}`
    : theme.spacing(0);

  return (
    <Box
      className="h-full"
      padding={gutterPadding}
      marginBottom={dotsPadding}
      position="relative"
      data-id="carousel-container"
    >
      <Swiper
        className="h-full"
        resizeObserver={true}
        loop={items.length > 1 ? loop : false}
        initialSlide={startAtIndex}
        breakpointsBase="container"
        breakpoints={breakpoints}
        slidesPerView={"auto"}
        onSwiper={setSwiperInstance}
        onSlideChange={(swiper) => {
          setActiveIndex(swiper.realIndex);
          onSlideChange?.(swiper.realIndex);
        }}
        modules={[Navigation, Pagination]}
        spaceBetween={spaceBetween}
        {...(speed !== undefined && { speed })}
        {...(effect !== undefined && { effect })}
        navigation={
          showArrows && items.length > 1
            ? { prevEl: prevRef.current, nextEl: nextRef.current }
            : false
        }
        pagination={false}
        touchRatio={1}
        touchAngle={45}
        touchMoveStopPropagation={true}
        preventInteractionOnTransition={true}
        allowTouchMove={items.length > 1}
        watchSlidesProgress={true}
        touchStartPreventDefault={true}
        touchStartForcePreventDefault={true}
        onTouchStart={() => {
          document.body.style.overflow = "hidden";
        }}
        onTouchEnd={() => {
          document.body.style.overflow = "";
        }}
        onInit={(swiper) => {
          if (showArrows && typeof swiper.params.navigation === "object") {
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;
            swiper.navigation.init();
            swiper.navigation.update();
          }
        }}
        style={{
          paddingBottom: showDots && (dots as Dots).paddingOnWrapper ? 16 : 0,
          touchAction: items.length > 1 ? "pan-y pinch-zoom" : "none",
          WebkitOverflowScrolling: "touch",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        {items.map((jsxElement, index) => (
          <SwiperSlide key={index}>
            <Box
              className={`${carouselItemClass} h-full`}
              display="flex"
              alignItems={slideStyle?.alignItems}
              justifyContent="center"
              padding={slidePadding}
              style={{
                WebkitTouchCallout: "none",
                WebkitUserSelect: "none",
                userSelect: "none",
                touchAction: "manipulation",
              }}
            >
              {jsxElement}
            </Box>
          </SwiperSlide>
        ))}
      </Swiper>

      {showArrows && items.length > 1 && (
        <>
          <div
            ref={prevRef}
            style={{
              position: "absolute",
              top: "50%",
              left: 0,
              transform: "translateY(-50%)",
              zIndex: 10,
            }}
          >
            <ArrowButton
              data-id="carousel-prev-button"
              aria-label="Previous slide"
              color={arrowColor}
              shape={arrowShape}
              direction="left"
            />
          </div>
          <div
            ref={nextRef}
            style={{
              position: "absolute",
              top: "50%",
              right: 0,
              transform: "translateY(-50%)",
              zIndex: 10,
            }}
          >
            <ArrowButton
              data-id="carousel-next-button"
              aria-label="Next slide"
              color={arrowColor}
              shape={arrowShape}
              direction="right"
            />
          </div>
        </>
      )}

      {showDots && items.length > 1 && (
        <CarouselDotsWrapper
          paddingOnWrapper={(dots as Dots).paddingOnWrapper ?? true}
          style={{ bottom: `-${dotsPadding}`, ...dotsStyle }}
          role="radiogroup"
          aria-label="Select carousel slide"
        >
          {items.map((_, index) => (
            <CarouselDot
              key={index}
              onClick={() => swiperInstance?.slideToLoop(index)}
              className={activeIndex === index ? "active" : ""}
              aria-label={`Show Slide ${index}`}
              aria-checked={activeIndex === index}
            />
          ))}
        </CarouselDotsWrapper>
      )}
    </Box>
  );
};
